import { useIntl } from 'react-intl';

import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/components/sidePanel/SidePanelBody';
import { badgeCardTypeOptions } from 'src/features/cards/utils/constants';
import { cardMessages } from 'src/features/cards/utils/messages';
import { CURRENCIES } from 'src/features/payStations/types';
import { statisticsMessages } from 'src/features/statistics/utils';
import { useParking } from 'src/providers/ParkingProvider';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import ImageModal from 'src/registry/ui/ImageModal';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { generalMessages } from 'src/utils/messages';

import type { VisitsStatistic } from 'src/features/statistics/types';

type ParkingVisitSidePanelProps = {
    visit?: VisitsStatistic;
};

type GenericSidePanelBodyAttributes<K extends keyof VisitsStatistic> = {
    control: VisitsStatistic[K];
    title: {
        id: string;
        defaultMessage: string;
    };
};

const ParkingVisitSidePanel = ({ visit }: ParkingVisitSidePanelProps) => {
    const { formatMessage, formatNumber } = useIntl();
    const { parkingId } = useParking();

    function GenericSidePanelBodyAttributes<K extends keyof VisitsStatistic>({
        control,
        title,
    }: GenericSidePanelBodyAttributes<K>) {
        return (
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(title)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={control} fallbackMessage={formatMessage(generalMessages.noValue)}>
                    {control}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
        );
    }

    return (
        <>
            {visit ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">{visit?.card_number}</DialogTitle>
                        <Badge theme={badgeCardTypeOptions[visit.card_type]?.theme}>
                            {formatMessage(badgeCardTypeOptions[visit.card_type]?.label)}
                        </Badge>
                    </div>
                    <DialogDescription>{visit?.entry_point}</DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}

            <DialogSeparator />

            <DialogBody>
                <div className="grid gap-2">
                    <div className="grid gap-2">
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cardMessages.entryPoint)}
                        </SidePanelBodyAttributeTitle>
                        <ImageModal
                            image={`/parkings/images/${parkingId}/${visit?.photo_path_entry}`}
                            alt={visit?.plate_number}
                            title={visit?.card_number}
                            disableClick={!visit?.photo_path_entry}
                        />
                    </div>

                    <div className="grid gap-2">
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cardMessages.exitPoint)}
                        </SidePanelBodyAttributeTitle>
                        <ImageModal
                            image={`/parkings/images/${parkingId}/${visit?.photo_path_exit}`}
                            alt={visit?.plate_number}
                            title={visit?.card_number}
                            disableClick={!visit?.photo_path_exit}
                        />
                    </div>
                </div>

                <div className="mt-4 grid gap-2">
                    <GenericSidePanelBodyAttributes control={visit?.card_number} title={cardMessages.cardNumber} />

                    <GenericSidePanelBodyAttributes control={visit?.owner_name} title={cardMessages.ownerName} />

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cardMessages.cardType)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={visit?.card_type}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {visit?.card_type && (
                                <Badge
                                    theme={badgeCardTypeOptions[visit.card_type]?.theme || 'gray'}
                                    className="gap-1.5 whitespace-nowrap"
                                >
                                    {formatMessage(badgeCardTypeOptions[visit.card_type]?.label)}
                                </Badge>
                            )}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cardMessages.enterDatetime)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={visit?.enter_datetime}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {getFormattedDateTimeForDetailPanel(visit?.enter_datetime)}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(statisticsMessages.exitDatetime)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={visit?.exit_datetime}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {getFormattedDateTimeForDetailPanel(visit?.exit_datetime)}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <GenericSidePanelBodyAttributes control={visit?.entry_point} title={cardMessages.entryPoint} />

                    <GenericSidePanelBodyAttributes control={visit?.exit_point} title={cardMessages.exitPoint} />

                    <GenericSidePanelBodyAttributes control={visit?.parking_name} title={cardMessages.parkingZone} />

                    <GenericSidePanelBodyAttributes control={visit?.charge_plan_name} title={cardMessages.chargePlan} />

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(statisticsMessages.paid)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control={visit?.price_paid} fallbackMessage="0,00 лв.">
                            {visit?.price_paid &&
                                formatNumber(visit.price_paid / 100, {
                                    style: 'currency',
                                    currency: visit.currency ?? CURRENCIES.BGN,
                                })}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(statisticsMessages.paidAt)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={visit?.price_paid_time}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {getFormattedDateTimeForDetailPanel(visit?.price_paid_time)}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                </div>
            </DialogBody>

            <DialogSeparator />

            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
};

export default ParkingVisitSidePanel;
